import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthContext';
import { YouTubeTokenResponse, YouTubeChannel, YouTubeContentType } from '../types/youtube.types';
import { exchangeYouTubeCodeForToken } from '../services/youtubeAuthentication';
import { debug } from "../../../../../utils/debugLogger";
import { validateOAuthState } from "../../../../../utils/oauthState";


interface YouTubeConnectionState {
  loading: boolean;
  error: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  email: string | null;
  channels: YouTubeChannel[];
  contentTypes: YouTubeContentType[];
}

// Using the imported YouTubeChannel type

export function useYouTubeConnection() {
  const location = useLocation();
  const { isAuthenticated, idToken } = useAuth();
  const [state, setState] = useState<YouTubeConnectionState>({
    loading: true,
    error: null,
    accessToken: null,
    refreshToken: null,
    email: null,
    channels: [],
    contentTypes: [],
  });

  useEffect(() => {
    const processCallback = async () => {
      try {
        debug.log("Processing YouTube callback...");
        const urlParams = new URLSearchParams(window.location.search);
        if (!validateOAuthState("youtube", urlParams.get("state"))) {
          throw new Error("Security check failed (invalid OAuth state). Please try connecting again.");
        }
        const code = urlParams.get("code");
        const error = urlParams.get("error");

        if (error) {
          throw new Error(error);
        }

        if (!code) {
          throw new Error("No authorization code received");
        }

        // Exchange the code for tokens
        const tokenData: YouTubeTokenResponse = await exchangeYouTubeCodeForToken(code);
        
        // Fetch channel data
        const channelResponse = await fetch(
          `https://youtube.googleapis.com/youtube/v3/channels?part=snippet,statistics&mine=true`,
          {
            headers: {
              Authorization: `Bearer ${tokenData.access_token}`,
            },
          }
        );

        if (!channelResponse.ok) {
          throw new Error('Failed to fetch channel data');
        }

        const channelData = await channelResponse.json();
        const channels = channelData.items.map((item: any) => ({
          id: item.id,
          title: item.snippet.title,
          description: item.snippet.description,
          thumbnails: {
            default: item.snippet.thumbnails.default,
            medium: item.snippet.thumbnails.medium,
            high: item.snippet.thumbnails.high,
          },
          statistics: {
            viewCount: item.statistics.viewCount,
            subscriberCount: item.statistics.subscriberCount,
            videoCount: item.statistics.videoCount,
            hiddenSubscriberCount: item.statistics.hiddenSubscriberCount,
          },
          contentTypes: [],
        }));

        // Fetch user info
        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v2/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenData.access_token}`,
            },
          }
        );

        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info');
        }

        const userInfo = await userInfoResponse.json();

        setState(prev => ({
          ...prev,
          loading: false,
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          email: userInfo.email,
          channels: channels
        }));
      } catch (error: any) {
        debug.error("YouTube authentication error:", error);
        setState(prev => ({
          ...prev,
          error: error.message || "Failed to connect to YouTube",
          loading: false,
        }));
      }
    };

    if (location.search) {
      processCallback();
    }
  }, [location.search]);

  return {
    ...state,
    isAuthenticated,
  };
}
